import type { FocusCause } from "./_focus-schema";

type Tone = "harder" | "softer";

export type FocusCopyPack = {
  /** Verdict pill text per cause, uppercase at render time. */
  label: Record<FocusCause, string>;
  headline: Record<FocusCause, string>;
  /**
   * `harder` serves HIGH and SEVERE; `softer` serves everything below. BOTH
   * must read as a problem: `softer` is what the DETECTED floor renders, and
   * DETECTED exists to confirm the problem rather than grade it politely.
   *
   * Three strings about the same cause render on one screen, so each one has a
   * DIFFERENT job and must not do another's: the `headline` names the state,
   * this carries the COST of it in his own week, and `causeBody` (in the
   * Primary Cause cell below) carries the mechanism. Until 2026-09-15 all
   * three said roughly the same sentence -- the harder explanations opened by
   * repeating their own headline almost verbatim ("pieces too small to think
   * in", "recall and processing speed have shifted"), and several also
   * restated the cause card ("nothing in your routine is compensating" beside
   * "most routines never adjust"). Three restatements read as a template bug,
   * not a diagnosis. Keep the jobs separate.
   */
  explanation: Record<FocusCause, Record<Tone, string>>;
  /**
   * One line per cause, rendered inside a cell of the 2x2 grid on a phone.
   * Held under 90 characters for that reason: four of these sit two-up.
   */
  causeBody: Record<FocusCause, string>;
  /** The all-clear branch. Sells the next level, never a repair. */
  headroomLabel: string;
  headroomHeadline: string;
  headroomExplanation: string;
};

/**
 * Every string here is written against the claims boundary in
 * docs/ignite-cognition-brief.md, section 2. No percentages, no "cures", no
 * "fights fatigue", no Adderall comparison, and nothing promising an effect
 * size. Explanations are held near 110 characters because they render inside
 * the fold-critical assessment card; tests/quizzes/quiz-focus-profile.test.ts
 * locks both the budget and the banned phrases.
 *
 * Verdict PROSE lives here. Verdict enum LABELS (MINIMAL, DISRUPTIVE, and the
 * like) will live in the widget's i18n pack when Plan 2 renders them -- the
 * same boundary rule 28 draws for the Boost verdict.
 */
export const EN_FOCUS_COPY: FocusCopyPack = {
  label: {
    "interruption-tax": "Interruption Tax",
    "multitasking-overload": "Multitasking Overload",
    "cognitive-depletion": "Cognitive Depletion",
    "age-related-decline": "Age-Related Decline",
  },
  headline: {
    "interruption-tax": "Your day arrives in pieces too small to think in",
    "multitasking-overload": "You are carrying more open threads than you can hold",
    "cognitive-depletion": "You are spending the morning's capacity before lunch",
    "age-related-decline": "Recall and processing speed have shifted on you",
  },
  explanation: {
    "interruption-tax": {
      harder:
        "The work that needs a long run at it keeps sliding to tomorrow while the shallow tasks fill the day.",
      softer:
        "The one task that needs real depth keeps sliding down the list while the easy ones get done first.",
    },
    "multitasking-overload": {
      harder:
        "You end most days busy and behind at once, with almost nothing you could point to as finished.",
      softer:
        "A full day can end with plenty touched and very little you would call finished.",
    },
    "cognitive-depletion": {
      harder:
        "Whatever lands after lunch gets the worst version of you, and by evening there is nothing left to give.",
      softer:
        "Anything that lands after lunch gets a thinner version of you than the morning would have handled.",
    },
    "age-related-decline": {
      harder:
        "Names and words arrive late, and you have built quiet workarounds so nobody else notices.",
      softer:
        "Names and words arrive a beat late, and you have started writing more down to cover it.",
    },
  },
  causeBody: {
    "interruption-tax":
      "About 23 minutes to rebuild a thought. Your day interrupts more often than that.",
    "multitasking-overload":
      "Task-switching trains the habit of abandoning whatever turns hard.",
    "cognitive-depletion":
      "The 3pm version of you runs on whatever the morning left over.",
    "age-related-decline":
      "Attention and recall shift after 45. Most routines never adjust.",
  },
  headroomLabel: "Performance Headroom",
  headroomHeadline: "You are holding your attention well",
  headroomExplanation:
    "What is left is not repair. It is how much sharper you could run from a base this steady.",
};
